import { Card, Tag, ProgressBar, Icon, Title, Text } from '@nimbus-ds/components'
import { ArrowDownIcon, ArrowupIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@nimbus-ds/icons'

interface FunnelStep {
  step_id: string
  step_name: string
  overall: {
    count: number
    percentage: number
  }
  control: {
    count: number
    percentage: number
    conversion_rate_reached?: number
  }
  treatment: {
    count: number
    percentage: number
    conversion_rate_reached?: number
  }
  lift_percentage?: number
  p_value?: number
  is_significant?: boolean
  confidence_level?: number
  bb_rope?: { p_better: number | null; p_worse: number | null; verdict: 'treatment_better' | 'control_better' | 'inconclusive'; rope: number }
}

// Per-step BB+ROPE verdict chip — the SAME engine as the experiment header, so the funnel never
// claims a confident win the header calls inconclusive. Replaces the per-step z-test significance badge.
// Role-aware: the change-under-test lives in the treatment for a positive experiment and in the
// control for a negative one (is_negative inverts the roles), so the verdict is phrased as
// hypothesis-vs-baseline rather than the raw variant name — otherwise "Control mejor" on a negative
// experiment reads as a loss when the change is actually winning.
function StepVerdict({ bb, isNegative }: { bb?: FunnelStep['bb_rope']; isNegative?: boolean }) {
  if (!bb || bb.p_better == null) {
    return <Tag appearance="neutral">Pocos datos</Tag>
  }
  if (bb.verdict !== 'treatment_better' && bb.verdict !== 'control_better') {
    return <Tag appearance="neutral">Sin diferencia concluyente</Tag>
  }
  const treatmentWon = bb.verdict === 'treatment_better'
  const hypothesisWon = isNegative ? !treatmentWon : treatmentWon
  return hypothesisWon ? (
    <Tag appearance="success">Hipótesis mejor</Tag>
  ) : (
    <Tag appearance="primary">Línea base mejor</Tag>
  )
}

interface FunnelMetricsCardProps {
  funnelData: FunnelStep[]
  /** Negative experiment: the change-under-test lives in the control variant, so the
   *  control box is the Hypothesis and the treatment box is the Baseline. */
  isNegative?: boolean
}

export function FunnelMetricsCard({ funnelData, isNegative = false }: FunnelMetricsCardProps) {
  // Role of each variant box. For a positive experiment control = baseline, treatment = hypothesis;
  // a negative experiment inverts that (see is_negative). Matches DecisionHelper's convention.
  const controlRole = isNegative ? 'Hipótesis' : 'Línea base'
  const treatmentRole = isNegative ? 'Línea base' : 'Hipótesis'
  if (!funnelData || funnelData.length === 0) {
    return (
      <Card>
        <Card.Header title="Métricas Detalladas del Funnel" />
        <Card.Body>
          <div className="text-center py-8">
            <Text>No hay datos de funnel disponibles</Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const calculateDropoff = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((previous - current) / previous) * 100
  }

  const calculateLift = (treatment: number, control: number) => {
    if (control === 0) return 0
    return ((treatment - control) / control) * 100
  }

  // End-to-end conversion per variant (last step reached / first step reached), then mapped to
  // roles so the summary lift is hypothesis-vs-baseline. For a negative experiment the change
  // lives in control, so a raw treatment-vs-control lift would carry the wrong sign AND denominator
  // (see economic-calculations.hypothesisLiftFromRates / DecisionHelper).
  const firstStep = funnelData[0]
  const lastStep = funnelData[funnelData.length - 1]
  const controlEndRate = firstStep.control.count > 0 ? (lastStep.control.count / firstStep.control.count) * 100 : 0
  const treatmentEndRate = firstStep.treatment.count > 0 ? (lastStep.treatment.count / firstStep.treatment.count) * 100 : 0
  const hypothesisEndRate = isNegative ? controlEndRate : treatmentEndRate
  const baselineEndRate = isNegative ? treatmentEndRate : controlEndRate
  const hypothesisLift = baselineEndRate > 0 ? ((hypothesisEndRate - baselineEndRate) / baselineEndRate) * 100 : 0

  return (
    <Card>
      <Card.Header>
        <Title as="h3">Análisis Detallado por Paso</Title>
        <Text>Comparación de métricas entre variantes en cada etapa del funnel</Text>
      </Card.Header>
      <Card.Body>
        <div className="space-y-6">
          {funnelData.map((step, index) => {
            const previousStep = index > 0 ? funnelData[index - 1] : null
            const controlDropoff = previousStep
              ? calculateDropoff(step.control.count, previousStep.control.count)
              : 0
            const treatmentDropoff = previousStep
              ? calculateDropoff(step.treatment.count, previousStep.treatment.count)
              : 0

            const countLift = calculateLift(step.treatment.count, step.control.count)
            const retentionLift = previousStep
              ? calculateLift(
                  (step.treatment.count / previousStep.treatment.count) * 100,
                  (step.control.count / previousStep.control.count) * 100
                )
              : 0

            const isSignificantDropoff = controlDropoff > 50 || treatmentDropoff > 50

            return (
              <div key={step.step_id} className="space-y-4">
                {/* Step header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <Title as="h4">{step.step_name}</Title>
                      <Text>
                        {/* Experiment-scoped total (sum of the variants). NOT step.overall.count —
                            that's the store-wide step_analysis count and over-reports in an experiment. */}
                        {(step.control.count + step.treatment.count).toLocaleString()} sesiones en el experimento
                      </Text>
                    </div>
                  </div>
                  {isSignificantDropoff && (
                    <div className="shrink-0 whitespace-nowrap">
                      <Tag appearance="danger">
                        <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
                        Alto drop-off
                      </Tag>
                    </div>
                  )}
                </div>

                {/* Variant comparison */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Control variant */}
                  <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                        Control
                        <Tag appearance="neutral">{controlRole}</Tag>
                      </span>
                      <div className="shrink-0 whitespace-nowrap">
                        <Tag appearance="neutral">{step.control.count.toLocaleString()}</Tag>
                      </div>
                    </div>
                    <ProgressBar value={step.control.percentage} appearance="primary" />
                    <div className="flex items-center justify-between">
                      <Text as="span" fontSize="caption">{step.control.percentage.toFixed(1)}% de sesiones</Text>
                      {previousStep && (
                        <span className="flex items-center gap-1">
                          <span className="text-amber-600"><Icon source={<ArrowDownIcon />} color="currentColor" /></span>
                          <Text as="span" fontSize="caption">{controlDropoff.toFixed(1)}% drop-off</Text>
                        </span>
                      )}
                    </div>
                    {/* Conversion-to-goal baseline: of control sessions that reached this step, % that converted. */}
                    <div className="flex items-center justify-between border-t border-blue-200/60 dark:border-blue-800/60 pt-2">
                      <Text as="span" fontSize="caption">Conversión a meta</Text>
                      <span className="text-xs font-medium tabular-nums text-blue-700 dark:text-blue-300">
                        {(step.control.conversion_rate_reached ?? 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Treatment variant */}
                  <div className="space-y-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
                        Tratamiento
                        <Tag appearance="neutral">{treatmentRole}</Tag>
                      </span>
                      <div className="shrink-0 whitespace-nowrap">
                        <Tag appearance="neutral">{step.treatment.count.toLocaleString()}</Tag>
                      </div>
                    </div>
                    <ProgressBar value={step.treatment.percentage} appearance="primary" />
                    <div className="flex items-center justify-between">
                      <Text as="span" fontSize="caption">{step.treatment.percentage.toFixed(1)}% de sesiones</Text>
                      {previousStep && (
                        <span className="flex items-center gap-1">
                          <span className="text-amber-600"><Icon source={<ArrowDownIcon />} color="currentColor" /></span>
                          <Text as="span" fontSize="caption">{treatmentDropoff.toFixed(1)}% drop-off</Text>
                        </span>
                      )}
                    </div>
                    {/* Conversion-to-goal baseline: of treatment sessions that reached this step, % that converted. */}
                    <div className="flex items-center justify-between border-t border-green-200/60 dark:border-green-800/60 pt-2">
                      <Text as="span" fontSize="caption">Conversión a meta</Text>
                      <span className="text-xs font-medium tabular-nums text-green-700 dark:text-green-300">
                        {(step.treatment.conversion_rate_reached ?? 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Comparative insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Text as="span">Diferencia en sesiones</Text>
                    <div className="flex items-center gap-2">
                      {/* Observed difference — DESCRIPTIVE only (muted), no longer a verdict signal. */}
                      <Text as="span" fontWeight="medium" className="tabular-nums">
                        {countLift > 0 ? '+' : ''}{countLift.toFixed(1)}%
                      </Text>
                      {/* The verdict is the BB+ROPE chip — consistent with the experiment header. */}
                      <StepVerdict bb={step.bb_rope} isNegative={isNegative} />
                    </div>
                  </div>
                  {previousStep && (
                    <div className="flex items-center justify-between">
                      <Text as="span">Lift en retención</Text>
                      <span className={`text-sm font-semibold flex items-center gap-1 ${
                        retentionLift > 0 ? 'text-green-600' : retentionLift < 0 ? 'text-red-600' : 'text-muted-foreground'
                      }`}>
                        {retentionLift > 0 ? (
                          <Icon source={<ArrowupIcon />} color="currentColor" />
                        ) : retentionLift < 0 ? (
                          <Icon source={<ArrowDownIcon />} color="currentColor" />
                        ) : null}
                        {retentionLift > 0 ? '+' : ''}{retentionLift.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Step separator */}
                {index < funnelData.length - 1 && (
                  <div className="flex items-center justify-center">
                    <span className="text-muted-foreground"><Icon source={<ArrowRightIcon />} color="currentColor" /></span>
                  </div>
                )}
              </div>
            )
          })}

          {/* Summary card */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-950/20 dark:to-green-950/20 rounded-lg border">
            <Title as="h4" className="mb-3">Resumen del Funnel</Title>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Text>Conversión · Control ({controlRole})</Text>
                <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                  {controlEndRate.toFixed(2)}%
                </p>
              </div>
              <div>
                <Text>Conversión · Tratamiento ({treatmentRole})</Text>
                <p className="text-lg font-bold text-green-700 dark:text-green-300">
                  {treatmentEndRate.toFixed(2)}%
                </p>
              </div>
              <div>
                {/* Hypothesis-vs-baseline lift — sign-correct for negative experiments. */}
                <Text>Lift (hipótesis)</Text>
                <p className={`text-lg font-bold ${hypothesisLift > 0 ? 'text-green-600' : hypothesisLift < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {hypothesisLift > 0 ? '+' : ''}{hypothesisLift.toFixed(2)}%
                </p>
              </div>
              <div>
                <Text>Paso Crítico</Text>
                <Text fontWeight="bold" color="neutral-textHigh">
                  {funnelData.reduce((critical, step, i) => {
                    if (i === 0) return critical
                    const prev = funnelData[i - 1]
                    const dropoff = calculateDropoff(step.overall.count, prev.overall.count)
                    return dropoff > critical.dropoff ? { step: step.step_name, dropoff } : critical
                  }, { step: 'N/A', dropoff: 0 }).step}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
